import { useState, useEffect, useMemo, useRef } from "react";
import { ConfirmCloseDialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/ConfirmCloseDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { MapPin, ExternalLink, Building2, Zap, Ruler, Users, X, Tag, Euro, Percent, Home, CalendarDays } from "lucide-react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useMapWithProvider } from "@/hooks/useMapWithProvider";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import {
  KAVEL_FASEN,
  STADIA_PER_FASE,
  faseVanStadium,
  stadiumLabel,
} from "@/lib/chargingStages";
import { assertRowsWritten } from "@/lib/assertRowsWritten";

const GREENVISION_CHARGING_ID = '22d7cffa-97b4-43c9-865e-8bfa339b95a4';

interface LocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  location?: any;
  initialStage?: string;
}

export function LocationModal({ isOpen, onClose, location, initialStage = 'prospect' }: LocationModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { MapComponent } = useMapWithProvider();
  const [loading, setLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  const [tagInput, setTagInput] = useState('');
  const [localNumericValues, setLocalNumericValues] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    latitude: null as number | null,
    longitude: null as number | null,
    available_power_kw: null as number | null,
    realtor_name: '',
    realtor_contact: '',
    plot_size_m2: null as number | null,
    development_size_chargers: null as number | null,
    market_listing_url: '',
    notes: '',
    org_id: null as string | null,
    stage: initialStage,
    locode: null as string | null,
    tags: [] as string[],
    // New fields
    ownership_type: null as 'koop' | 'huur' | 'huur_koopoptie' | 'in_eigendom' | null,
    surface_area_m2: null as number | null,
    contracted_power_kw: null as number | null,
    total_investment: null as number | null,
    equity_percentage: null as number | null,
    cost_land_purchase: null as number | null,
    cost_land_development: null as number | null,
    cost_charging_infra: null as number | null,
    cost_engineering: null as number | null,
    target_quarter: null as string | null,
    subsidy_amount: null as number | null,
  });

  const { hasUnsavedChanges, resetInitialValues } = useUnsavedChanges(formData, isOpen);

  // Fetch all existing tags for suggestions
  const { data: allLocations = [] } = useQuery({
    queryKey: ['all-charging-locations-tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select('tags')
        .is('deleted_at', null);
      
      if (error) throw error;
      return data || [];
    },
  });

  const existingTags = useMemo(() => {
    const allTags = allLocations.flatMap((l: any) => l.tags || []);
    return [...new Set(allTags)].sort();
  }, [allLocations]);

  // Track which location was last loaded to prevent re-initialization during typing
  const lastLoadedRef = useRef<{ isOpen: boolean; locationId: string | null }>({ isOpen: false, locationId: null });

  useEffect(() => {
    // Only initialize when modal opens or location changes
    const currentLocationId = location?.id ?? null;
    const wasJustOpened = isOpen && !lastLoadedRef.current.isOpen;
    const locationChanged = isOpen && currentLocationId !== lastLoadedRef.current.locationId;

    // Update ref
    lastLoadedRef.current = { isOpen, locationId: currentLocationId };

    // Skip if modal is closed or if this is just a re-render (not a real open/change)
    if (!isOpen) return;
    if (!wasJustOpened && !locationChanged) return;

    if (location) {
      setFormData({
        name: location.name || '',
        address: location.address || '',
        city: location.city || '',
        postal_code: location.postal_code || '',
        latitude: location.latitude || null,
        longitude: location.longitude || null,
        available_power_kw: location.available_power_kw || null,
        realtor_name: location.realtor_name || '',
        realtor_contact: location.realtor_contact || '',
        plot_size_m2: location.plot_size_m2 || null,
        development_size_chargers: location.development_size_chargers || null,
        market_listing_url: location.market_listing_url || '',
        notes: location.notes || '',
        org_id: location.org_id || null,
        stage: location.stage || initialStage,
        locode: location.locode || null,
        tags: location.tags || [],
        // New fields
        ownership_type: location.ownership_type || null,
        surface_area_m2: location.surface_area_m2 || null,
        contracted_power_kw: location.contracted_power_kw || null,
        total_investment: location.total_investment || null,
        equity_percentage: location.equity_percentage || null,
        cost_land_purchase: location.cost_land_purchase || null,
        cost_land_development: location.cost_land_development || null,
        cost_charging_infra: location.cost_charging_infra || null,
        cost_engineering: location.cost_engineering || null,
        target_quarter: location.target_quarter || null,
        subsidy_amount: location.subsidy_amount || null,
      });
      // Sync local numeric values for string-based input (all numeric fields)
      setLocalNumericValues({
        latitude: location.latitude?.toString() ?? '',
        longitude: location.longitude?.toString() ?? '',
        available_power_kw: location.available_power_kw?.toString() ?? '',
        development_size_chargers: location.development_size_chargers?.toString() ?? '',
        plot_size_m2: location.plot_size_m2?.toString() ?? '',
        surface_area_m2: location.surface_area_m2?.toString() ?? '',
        contracted_power_kw: location.contracted_power_kw?.toString() ?? '',
        total_investment: location.total_investment?.toString() ?? '',
        equity_percentage: location.equity_percentage?.toString() ?? '',
        cost_land_purchase: location.cost_land_purchase?.toString() ?? '',
        cost_land_development: location.cost_land_development?.toString() ?? '',
        cost_charging_infra: location.cost_charging_infra?.toString() ?? '',
        cost_engineering: location.cost_engineering?.toString() ?? '',
        subsidy_amount: location.subsidy_amount?.toString() ?? '',
      });
    } else {
      setFormData({
        name: '',
        address: '',
        city: '',
        postal_code: '',
        latitude: null,
        longitude: null,
        available_power_kw: null,
        realtor_name: '',
        realtor_contact: '',
        plot_size_m2: null,
        development_size_chargers: null,
        market_listing_url: '',
        notes: '',
        org_id: null,
        stage: initialStage,
        locode: null,
        tags: [],
        // New fields
        ownership_type: null,
        surface_area_m2: null,
        contracted_power_kw: null,
        total_investment: null,
        equity_percentage: null,
        cost_land_purchase: null,
        cost_land_development: null,
        cost_charging_infra: null,
        cost_engineering: null,
        target_quarter: null,
        subsidy_amount: null,
      });
      // Reset local numeric values (all numeric fields)
      setLocalNumericValues({
        latitude: '',
        longitude: '',
        available_power_kw: '',
        development_size_chargers: '',
        plot_size_m2: '',
        surface_area_m2: '',
        contracted_power_kw: '',
        total_investment: '',
        equity_percentage: '',
        cost_land_purchase: '',
        cost_land_development: '',
        cost_charging_infra: '',
        cost_engineering: '',
        subsidy_amount: '',
      });
    }
    setTagInput('');
    // Reset unsaved changes tracker after loading
    setTimeout(() => resetInitialValues(), 100);
  }, [location?.id, isOpen, initialStage, resetInitialValues]);

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim().toUpperCase();
    if (!trimmedTag) return;
    
    if (formData.tags.includes(trimmedTag)) {
      toast({
        title: "Tag bestaat al",
        description: "Deze tag is al toegevoegd",
        variant: "destructive"
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, trimmedTag]
    }));
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddExistingTag = (tag: string) => {
    if (formData.tags.includes(tag)) return;
    
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, tag]
    }));
  };

  const geocodeAddress = async () => {
    // Use city as fallback if address is empty
    const searchAddress = formData.address || formData.city;
    
    if (!searchAddress) {
      toast({
        title: "Plaats vereist",
        description: "Voer minimaal een plaats in om te geocoden.",
        variant: "destructive"
      });
      return;
    }

    setGeocoding(true);
    try {
      const query = formData.address 
        ? `${formData.address}, ${formData.city || ''}, Netherlands`
        : `${formData.city}, Netherlands`;
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`
      );
      const data = await response.json();

      if (data && data[0]) {
        setFormData(prev => ({
          ...prev,
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon)
        }));
        toast({
          title: "Coördinaten gevonden",
          description: "Locatie succesvol geocodeerd"
        });
      } else {
        toast({
          title: "Geen resultaten",
          description: "Geen coördinaten gevonden voor dit adres",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Geocoding fout",
        description: "Kon adres niet geocoden",
        variant: "destructive"
      });
    } finally {
      setGeocoding(false);
    }
  };

  const handleSave = async () => {
    // Only city is required, address is optional (will geocode to city center if missing)
    if (!formData.name || !formData.city) {
      toast({
        title: "Verplichte velden",
        description: "Naam en plaats zijn verplicht",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
    // Base payload without created_by - that should only be set on INSERT
    //
    // T2.5 / scepticus-ronde — `stage` stond hier NA de spread en overschreef
    // `formData.stage` met de oude waarde. De keuzelijst voor het stadium
    // werkte daardoor niet: je koos een stadium, kreeg "Locatie bijgewerkt",
    // en er veranderde niets. Nu wint de keuze van de gebruiker, en vallen we
    // alleen terug op de oude waarde (bewerken) of de meegegeven waarde
    // (aanmaken) als het formulier niets heeft staan.
    const basePayload = {
      ...formData,
      tenant_id: GREENVISION_CHARGING_ID,
      stage: formData.stage || location?.stage || initialStage,
      // `status` staat bewust NIET in het formulier (de kolom is dood: 34 van
      // de 34 rijen staan op 'new', zie migratie 084). Hier dus geen
      // formData-tak — die zou een veld suggereren dat niet bestaat.
      status: location?.status || 'new',
    };

      if (location) {
        // UPDATE - don't include created_by to avoid RLS issues
        const { data: bijgewerkt, error } = await supabase
          .from('charging_locations')
          .update(basePayload)
          .eq('id', location.id)
          .select('id');

        if (error) throw error;
        // Een door RLS geblokkeerde UPDATE geeft geen fout maar nul rijen.
        // Zonder deze telling meldde het scherm succes voor een save die
        // nooit is gebeurd.
        assertRowsWritten(bijgewerkt, 'Locatie bijwerken');

        toast({
          title: "Locatie bijgewerkt",
          description: "De locatie is succesvol bijgewerkt"
        });
      } else {
        // Get max order in the initial stage for new location
        const { data: maxOrderLocation } = await supabase
          .from('charging_locations')
          .select('order')
          .eq('stage', initialStage)
          .is('deleted_at', null)
          .order('order', { ascending: false })
          .limit(1)
          .maybeSingle();

        const newOrder = (maxOrderLocation?.order ?? -1) + 1;

        // INSERT - include created_by
        const { data: aangemaakt, error } = await supabase
          .from('charging_locations')
          .insert([{ ...basePayload, created_by: user?.id, order: newOrder }])
          .select('id');

        if (error) throw error;
        // Ook een INSERT kan door de WITH CHECK van de policy stil op nul
        // rijen uitkomen.
        assertRowsWritten(aangemaakt, 'Locatie aanmaken');

        toast({
          title: "Locatie toegevoegd",
          description: "De nieuwe locatie is succesvol toegevoegd"
        });
      }

      resetInitialValues();
      queryClient.invalidateQueries({ queryKey: ['charging-locations'] });
      onClose();
    } catch (error: any) {
      toast({
        title: "Fout bij opslaan",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // STABLE mapSites - only depends on lat/lng to prevent map re-render on every keystroke
  const mapSites = useMemo(() => {
    if (!formData.latitude || !formData.longitude) return [];
    return [{
      id: 'preview',
      name: 'Preview',
      address: '',
      latitude: formData.latitude,
      longitude: formData.longitude,
    }];
  }, [formData.latitude, formData.longitude]);

  return (
    <ConfirmCloseDialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      hasUnsavedChanges={hasUnsavedChanges}
      onConfirmDiscard={onClose}
      onConfirmSave={handleSave}
      isSaving={loading}
    >
      <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {location ? 'Locatie bewerken' : 'Nieuwe locatie'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Map Preview */}
          {formData.latitude && formData.longitude && (
            <div className="h-[300px] rounded-lg overflow-hidden border">
              <MapComponent sites={mapSites} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {/* Basic Info */}
            <div className="space-y-2">
              <Label htmlFor="name">Naam locatie *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Bijv. Laadplein Amsterdam Noord"
              />
            </div>

            {location?.locode && (
              <div className="space-y-2">
                <Label>LOCODE</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono bg-primary/10 text-primary px-3 py-1.5 rounded-md">
                    {location.locode}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    (Automatisch gegenereerd)
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="org_id">Organisatie</Label>
              <Input
                id="org_id"
                value={formData.org_id || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, org_id: e.target.value || null }))}
                placeholder="Organisatie ID (optioneel)"
              />
            </div>

            {/* Address */}
            <div className="space-y-2 col-span-2">
              <Label htmlFor="address">Adres (optioneel)</Label>
              <div className="flex gap-2">
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Straat en huisnummer"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={geocodeAddress}
                  disabled={geocoding}
                >
                  {geocoding ? 'Bezig...' : 'Geocode'}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="postal_code">Postcode</Label>
              <Input
                id="postal_code"
                value={formData.postal_code}
                onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                placeholder="1234 AB"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">Plaats *</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                placeholder="Amsterdam"
              />
            </div>

          {/* Coordinates */}
            <div className="space-y-2">
              <Label htmlFor="latitude">Latitude</Label>
              <Input
                id="latitude"
                type="text"
                inputMode="decimal"
                value={localNumericValues.latitude ?? ''}
                onChange={(e) => {
                  // Allow minus, digits, dots and commas for Dutch input
                  const val = e.target.value.replace(/[^0-9.,\-]/g, '');
                  setLocalNumericValues(prev => ({ ...prev, latitude: val }));
                }}
                onBlur={(e) => {
                  const val = e.target.value.replace(',', '.');
                  const parsed = parseFloat(val);
                  setFormData(prev => ({ ...prev, latitude: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                }}
                placeholder="52.3676"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="longitude">Longitude</Label>
              <Input
                id="longitude"
                type="text"
                inputMode="decimal"
                value={localNumericValues.longitude ?? ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.,\-]/g, '');
                  setLocalNumericValues(prev => ({ ...prev, longitude: val }));
                }}
                onBlur={(e) => {
                  const val = e.target.value.replace(',', '.');
                  const parsed = parseFloat(val);
                  setFormData(prev => ({ ...prev, longitude: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                }}
                placeholder="4.9041"
              />
            </div>

            {/* Charging Hub Details */}
            <div className="space-y-2">
              <Label htmlFor="available_power_kw" className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Beschikbare stroom (kW)
              </Label>
              <Input
                id="available_power_kw"
                type="text"
                inputMode="numeric"
                value={localNumericValues.available_power_kw ?? ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setLocalNumericValues(prev => ({ ...prev, available_power_kw: val }));
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  const parsed = parseInt(val, 10);
                  setFormData(prev => ({ ...prev, available_power_kw: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                }}
                placeholder="500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="development_size_chargers" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Aantal laadpalen
              </Label>
              <Input
                id="development_size_chargers"
                type="text"
                inputMode="numeric"
                value={localNumericValues.development_size_chargers ?? ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setLocalNumericValues(prev => ({ ...prev, development_size_chargers: val }));
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  const parsed = parseInt(val, 10);
                  setFormData(prev => ({ ...prev, development_size_chargers: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                }}
                placeholder="20"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="plot_size_m2" className="flex items-center gap-2">
                <Ruler className="h-4 w-4" />
                Grootte perceel (m²)
              </Label>
              <Input
                id="plot_size_m2"
                type="text"
                inputMode="numeric"
                value={localNumericValues.plot_size_m2 ?? ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setLocalNumericValues(prev => ({ ...prev, plot_size_m2: val }));
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  const parsed = parseInt(val, 10);
                  setFormData(prev => ({ ...prev, plot_size_m2: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                }}
                placeholder="2000"
              />
            </div>

            {/* Realtor Info */}
            <div className="space-y-2">
              <Label htmlFor="realtor_name" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Makelaar
              </Label>
              <Input
                id="realtor_name"
                value={formData.realtor_name}
                onChange={(e) => setFormData(prev => ({ ...prev, realtor_name: e.target.value }))}
                placeholder="Naam makelaar"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="realtor_contact">Contact makelaar</Label>
              <Input
                id="realtor_contact"
                value={formData.realtor_contact}
                onChange={(e) => setFormData(prev => ({ ...prev, realtor_contact: e.target.value }))}
                placeholder="email@example.com of 06-12345678"
              />
            </div>

            {/* Market Listing */}
            <div className="space-y-2 col-span-2">
              <Label htmlFor="market_listing_url" className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                Link verkoopartikel
              </Label>
              <Input
                id="market_listing_url"
                type="url"
                value={formData.market_listing_url}
                onChange={(e) => setFormData(prev => ({ ...prev, market_listing_url: e.target.value }))}
                placeholder="https://..."
              />
            </div>

            {/* Eigendom & Investering Sectie */}
            <div className="col-span-2 border-t pt-4 mt-2">
              <h4 className="font-medium mb-4 flex items-center gap-2">
                <Euro className="h-4 w-4" />
                Eigendom & Investering
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ownership_type" className="flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    Eigendomstype
                  </Label>
                  <Select
                    value={formData.ownership_type || ''}
                    onValueChange={(value) => setFormData(prev => ({ 
                      ...prev, 
                      ownership_type: value as 'koop' | 'huur' | 'huur_koopoptie' | 'in_eigendom' 
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecteer..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_eigendom">In eigendom</SelectItem>
                      <SelectItem value="koop">Koop</SelectItem>
                      <SelectItem value="huur">Huur</SelectItem>
                      <SelectItem value="huur_koopoptie">Huur met koopoptie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="surface_area_m2" className="flex items-center gap-2">
                    <Ruler className="h-4 w-4" />
                    Oppervlakte (m²)
                  </Label>
                  <Input
                    id="surface_area_m2"
                    type="text"
                    inputMode="decimal"
                    value={localNumericValues.surface_area_m2 ?? ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      setLocalNumericValues(prev => ({ ...prev, surface_area_m2: val }));
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      const parsed = parseFloat(val);
                      setFormData(prev => ({ ...prev, surface_area_m2: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                    }}
                    placeholder="2500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contracted_power_kw" className="flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Gecontracteerd vermogen (kW)
                  </Label>
                  <Input
                    id="contracted_power_kw"
                    type="text"
                    inputMode="decimal"
                    value={localNumericValues.contracted_power_kw ?? ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      setLocalNumericValues(prev => ({ ...prev, contracted_power_kw: val }));
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      const parsed = parseFloat(val);
                      setFormData(prev => ({ ...prev, contracted_power_kw: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                    }}
                    placeholder="1250"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="total_investment" className="flex items-center gap-2">
                    <Euro className="h-4 w-4" />
                    Totale investering (€)
                  </Label>
                  <Input
                    id="total_investment"
                    type="text"
                    inputMode="decimal"
                    value={localNumericValues.total_investment ?? ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      setLocalNumericValues(prev => ({ ...prev, total_investment: val }));
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      const parsed = parseFloat(val);
                      setFormData(prev => ({ ...prev, total_investment: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                    }}
                    placeholder="1500000"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="equity_percentage" className="flex items-center gap-2">
                    <Percent className="h-4 w-4" />
                    Equity behoefte (%)
                  </Label>
                  <Input
                    id="equity_percentage"
                    type="text"
                    inputMode="decimal"
                    value={localNumericValues.equity_percentage ?? ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      setLocalNumericValues(prev => ({ ...prev, equity_percentage: val }));
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      const parsed = parseFloat(val);
                      const clampedVal = isNaN(parsed) ? null : Math.min(100, Math.max(0, parsed));
                      setFormData(prev => ({ ...prev, equity_percentage: val === '' ? null : clampedVal }));
                    }}
                    placeholder="60"
                  />
                </div>

                {/* Calculated fields */}
                {formData.total_investment && formData.equity_percentage !== null && (
                  <div className="col-span-2 bg-muted/50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Equity deel:</span>
                      <span className="font-medium">
                        €{((formData.total_investment * (formData.equity_percentage || 0)) / 100).toLocaleString('nl-NL', { maximumFractionDigits: 0 })}
                        <span className="text-muted-foreground ml-1">({formData.equity_percentage}%)</span>
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Gefinancierd deel:</span>
                      <span className="font-medium">
                        €{((formData.total_investment * (100 - (formData.equity_percentage || 0))) / 100).toLocaleString('nl-NL', { maximumFractionDigits: 0 })}
                        <span className="text-muted-foreground ml-1">({100 - (formData.equity_percentage || 0)}%)</span>
                      </span>
                    </div>
                  </div>
                )}

                <div className="col-span-2 border-t pt-3 mt-2">
                  <Label className="text-sm text-muted-foreground mb-3 block">Kostenverdeling</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="cost_land_purchase" className="text-xs">Aankoop grond (€)</Label>
                      <Input
                        id="cost_land_purchase"
                        type="text"
                        inputMode="decimal"
                        value={localNumericValues.cost_land_purchase ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, '');
                          setLocalNumericValues(prev => ({ ...prev, cost_land_purchase: val }));
                        }}
                        onBlur={(e) => {
                          const val = e.target.value;
                          const parsed = parseFloat(val);
                          setFormData(prev => ({ ...prev, cost_land_purchase: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                        }}
                        placeholder="400000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cost_land_development" className="text-xs">Aanleg grond (€)</Label>
                      <Input
                        id="cost_land_development"
                        type="text"
                        inputMode="decimal"
                        value={localNumericValues.cost_land_development ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, '');
                          setLocalNumericValues(prev => ({ ...prev, cost_land_development: val }));
                        }}
                        onBlur={(e) => {
                          const val = e.target.value;
                          const parsed = parseFloat(val);
                          setFormData(prev => ({ ...prev, cost_land_development: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                        }}
                        placeholder="300000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cost_charging_infra" className="text-xs">Laadinfra (€)</Label>
                      <Input
                        id="cost_charging_infra"
                        type="text"
                        inputMode="decimal"
                        value={localNumericValues.cost_charging_infra ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, '');
                          setLocalNumericValues(prev => ({ ...prev, cost_charging_infra: val }));
                        }}
                        onBlur={(e) => {
                          const val = e.target.value;
                          const parsed = parseFloat(val);
                          setFormData(prev => ({ ...prev, cost_charging_infra: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                        }}
                        placeholder="800000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cost_engineering" className="text-xs">Engineering (€)</Label>
                      <Input
                        id="cost_engineering"
                        type="text"
                        inputMode="decimal"
                        value={localNumericValues.cost_engineering ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, '');
                          setLocalNumericValues(prev => ({ ...prev, cost_engineering: val }));
                        }}
                        onBlur={(e) => {
                          const val = e.target.value;
                          const parsed = parseFloat(val);
                          setFormData(prev => ({ ...prev, cost_engineering: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                        }}
                        placeholder="50000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subsidy_amount" className="text-xs">Subsidie (€)</Label>
                      <Input
                        id="subsidy_amount"
                        type="text"
                        inputMode="decimal"
                        value={localNumericValues.subsidy_amount ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, '');
                          setLocalNumericValues(prev => ({ ...prev, subsidy_amount: val }));
                        }}
                        onBlur={(e) => {
                          const val = e.target.value;
                          const parsed = parseFloat(val);
                          setFormData(prev => ({ ...prev, subsidy_amount: val === '' ? null : (isNaN(parsed) ? null : parsed) }));
                        }}
                        placeholder="150000"
                      />
                    </div>
                  </div>
                </div>

                {/* Target Quarter / Timing */}
                <div className="space-y-2">
                  <Label htmlFor="target_quarter" className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    Timing (target)
                  </Label>
                  <Select
                    value={formData.target_quarter || ''}
                    onValueChange={(value) => setFormData(prev => ({ 
                      ...prev, 
                      target_quarter: value || null
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecteer kwartaal..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Q1 2026">Q1 2026</SelectItem>
                      <SelectItem value="Q2 2026">Q2 2026</SelectItem>
                      <SelectItem value="Q3 2026">Q3 2026</SelectItem>
                      <SelectItem value="Q4 2026">Q4 2026</SelectItem>
                      <SelectItem value="Q1 2027">Q1 2027</SelectItem>
                      <SelectItem value="Q2 2027">Q2 2027</SelectItem>
                      <SelectItem value="Q3 2027">Q3 2027</SelectItem>
                      <SelectItem value="Q4 2027">Q4 2027</SelectItem>
                      <SelectItem value="Q1 2028">Q1 2028</SelectItem>
                      <SelectItem value="Q2 2028">Q2 2028</SelectItem>
                      <SelectItem value="Q3 2028">Q3 2028</SelectItem>
                      <SelectItem value="Q4 2028">Q4 2028</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-2 col-span-2">
              <Label htmlFor="tags" className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Tags
              </Label>
              <div className="flex gap-2">
                <Input 
                  id="tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Tag toevoegen (bijv. SPULA)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={handleAddTag}>
                  Toevoegen
                </Button>
              </div>
              
              {/* Current tags */}
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-destructive" 
                        onClick={() => handleRemoveTag(tag)} 
                      />
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Existing tag suggestions */}
              {existingTags.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground mb-1">Bestaande tags:</p>
                  <div className="flex flex-wrap gap-1">
                    {existingTags
                      .filter(tag => !formData.tags.includes(tag))
                      .map((tag) => (
                        <Badge 
                          key={tag} 
                          variant="outline" 
                          className="cursor-pointer hover:bg-accent text-xs"
                          onClick={() => handleAddExistingTag(tag)}
                        >
                          + {tag}
                        </Badge>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Stage Selector */}
            <div className="space-y-2 col-span-2">
              <Label htmlFor="stage">Status</Label>
              <Select 
                value={formData.stage} 
                onValueChange={(value) => setFormData({ ...formData, stage: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer status" />
                </SelectTrigger>
                {/* De elf stadia, gegroepeerd onder hun fase (T2.5) — hier
                    zie je de indeling waar de lijst op groepeert. Eén lijst,
                    uit src/lib/chargingStages.ts: een stadium dat daar wordt
                    toegevoegd verschijnt vanzelf hier. */}
                <SelectContent>
                  {KAVEL_FASEN.map((fase) => (
                    <SelectGroup key={fase.id}>
                      <SelectLabel>{fase.label}</SelectLabel>
                      {STADIA_PER_FASE[fase.id].map((stadium) => (
                        <SelectItem key={stadium} value={stadium}>
                          {stadiumLabel(stadium)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                  {/* Staat er een waarde in de rij die in geen enkele fase
                      valt — 'prospect' is de default van de kolom en hoort bij
                      geen van de elf — dan hoort hij hier te staan. Zonder
                      deze regel toont de Select een lege trigger: de Kavel
                      heeft wel een stadium, maar het formulier laat het niet
                      zien, en je weet dus niet wat je wijzigt. */}
                  {faseVanStadium(formData.stage) === 'onbekend' && formData.stage && (
                    <SelectGroup>
                      <SelectLabel>Niet ingedeeld</SelectLabel>
                      <SelectItem value={formData.stage}>
                        {stadiumLabel(formData.stage)}
                      </SelectItem>
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Je kunt de status terug- of vooruitschroeven naar elke fase
              </p>
            </div>

            {/* Notes */}
            <div className="space-y-2 col-span-2">
              <Label htmlFor="notes">Notities</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Extra informatie over deze locatie..."
                rows={4}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Annuleren
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              {loading ? 'Bezig...' : 'Opslaan'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </ConfirmCloseDialog>
  );
}
